import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Activity, Server, Network, DatabaseBackup, Cloud, ScanFace, ShieldCheck } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { useI18n } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];
const BARS = 60; // ~ ultimele 60 de zile

// Generator determinist per serviciu — arată realist, dar stabil între randări.
const makeHistory = (seed, incidents) => {
  const out = [];
  let s = seed;
  for (let i = 0; i < BARS; i++) {
    s = (s * 9301 + 49297) % 233280;
    const r = s / 233280;
    let status = "ok";
    if (incidents.includes(i)) status = "down";
    else if (r > 0.965) status = "warn";
    out.push(status);
  }
  return out;
};

const SERVICES = [
  { id: "srv", icon: Server, nameKey: "st.s.srv", uptime: "99.99%", seed: 137, incidents: [] },
  { id: "net", icon: Network, nameKey: "st.s.net", uptime: "99.98%", seed: 913, incidents: [41] },
  { id: "bkp", icon: DatabaseBackup, nameKey: "st.s.bkp", uptime: "100%", seed: 271, incidents: [] },
  { id: "mon", icon: Activity, nameKey: "st.s.mon", uptime: "99.97%", seed: 553, incidents: [22] },
  { id: "cloud", icon: Cloud, nameKey: "st.s.cloud", uptime: "99.96%", seed: 689, incidents: [12, 13] },
  { id: "fw", icon: ShieldCheck, nameKey: "st.s.fw", uptime: "100%", seed: 401, incidents: [] },
  { id: "acc", icon: ScanFace, nameKey: "st.s.acc", uptime: "99.99%", seed: 827, incidents: [] },
];

const COLORS = { ok: "#34d399", warn: "#f59e0b", down: "#ff5b7a" };

const UptimeBars = ({ history }) => (
  <div className="flex h-9 items-end gap-[2px]" data-testid="uptime-bars">
    {history.map((st, i) => (
      <motion.span
        key={i}
        initial={{ scaleY: 0.3, opacity: 0 }}
        whileInView={{ scaleY: 1, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.3, delay: i * 0.004 }}
        title={st === "ok" ? "Operațional" : st === "warn" ? "Degradat" : "Incident"}
        className="group/bar h-full flex-1 origin-bottom rounded-[1px] transition-transform duration-150 hover:scale-y-110"
        style={{ backgroundColor: COLORS[st], opacity: st === "ok" ? 0.55 : 0.9 }}
      />
    ))}
  </div>
);

const ServiceRow = ({ s, i }) => {
  const history = useMemo(() => makeHistory(s.seed, s.incidents), [s]);
  const Icon = s.icon;
  const { t } = useI18n();
  const hasIssue = s.incidents.length > 0;
  return (
    <motion.div
      data-testid={`status-service-${s.id}`}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: EASE, delay: i * 0.05 }}
      className="group grid grid-cols-1 gap-4 border-b border-white/5 py-5 last:border-0 sm:grid-cols-[1.1fr_2fr_auto] sm:items-center"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-white/10 text-white/50 transition-colors duration-300 group-hover:text-cyan-neon">
          <Icon size={16} strokeWidth={1.7} />
        </span>
        <span className="font-display text-sm font-semibold text-white">{t(s.nameKey)}</span>
      </div>

      <UptimeBars history={history} />

      <div className="flex items-center justify-between gap-4 sm:justify-end">
        <span className="font-mono text-xs text-white/40">{s.uptime}</span>
        <span
          className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest"
          style={{
            color: hasIssue ? COLORS.warn : COLORS.ok,
            borderColor: `${hasIssue ? COLORS.warn : COLORS.ok}40`,
            background: `${hasIssue ? COLORS.warn : COLORS.ok}12`,
          }}
        >
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: hasIssue ? COLORS.warn : COLORS.ok, boxShadow: `0 0 8px ${hasIssue ? COLORS.warn : COLORS.ok}` }}
          />
          {hasIssue ? t("st.badge.mon") : t("st.badge.ok")}
        </span>
      </div>
    </motion.div>
  );
};

export const StatusPage = () => {
  const [ping, setPing] = useState(23);
  const [reqs, setReqs] = useState(1284502);
  const { t } = useI18n();

  useEffect(() => {
    const id = setInterval(() => {
      setPing((p) => Math.max(11, Math.min(48, Math.round(p + (Math.random() - 0.5) * 8))));
      setReqs((r) => r + Math.floor(Math.random() * 40 + 5));
    }, 1800);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="status"
      data-testid="status-section"
      className="relative overflow-hidden border-t border-white/10 bg-ink-900"
    >
      <div className="pointer-events-none absolute -left-40 top-10 h-[32rem] w-[32rem] rounded-full bg-emerald-400/8 blur-[150px]" />
      <div className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="max-w-3xl">
          <Reveal><SectionLabel index="[ • ]">{t("st.label")}</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {t("st.h1")} <span className="text-aurora">{t("st.hkw")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-white/55">
              {t("st.sub")}
            </p>
          </Reveal>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mt-12"
        >
          {/* Overall banner */}
          <div className="card-edge flex flex-col gap-4 rounded-2xl p-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <span className="relative grid h-14 w-14 shrink-0 place-items-center rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-400">
                <CheckCircle2 size={26} />
                <span className="absolute inset-0 animate-ping rounded-full border border-emerald-400/40" />
              </span>
              <div>
                <div className="font-display text-lg font-bold text-white">{t("st.allok")}</div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-white/40">
                  {t("st.lastcheck")}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10">
              {[
                { v: "99.98%", l: t("st.k.uptime"), c: "#34d399" },
                { v: `${ping} ms`, l: t("st.k.resp"), c: "#00E5FF" },
                { v: reqs.toLocaleString("ro-RO"), l: t("st.k.req"), c: "#7C9CFC" },
              ].map((k) => (
                <div key={k.l} className="bg-ink-900/70 px-5 py-4 text-center">
                  <div className="font-display text-lg font-extrabold" style={{ color: k.c }}>{k.v}</div>
                  <div className="mt-0.5 font-mono text-[9px] uppercase tracking-widest text-white/40">{k.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Services list */}
          <div className="card-edge mt-4 rounded-2xl px-6 py-2 sm:px-8">
            {SERVICES.map((s, i) => (
              <ServiceRow key={s.id} s={s} i={i} />
            ))}
          </div>

          {/* Legend */}
          <div className="mt-5 flex flex-wrap items-center gap-5 font-mono text-[10px] uppercase tracking-widest text-white/40">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: COLORS.ok }} /> {t("st.legend.ok")}</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: COLORS.warn }} /> {t("st.legend.warn")}</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: COLORS.down }} /> {t("st.legend.down")}</span>
            <span className="ml-auto text-white/30">{t("st.footer")}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
